import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useGlobalContext } from '../context/GlobalState'
import { UserAuth } from '../context/AuthContext'
import { useQueryGetAllBusinesses } from '../queries/businessQueries'
import { useQueryGetUserMe } from '../queries/userQueries'
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard'
import SubscriptionBlockModal from '../components/modules/subscription/SubscriptionBlockModal'
import {
  getRegistrationPath,
  REGISTRATION_STATUS
} from '../utils/authSession'

const DISABLED_ACCOUNT_MODAL = {
  title: 'Cuenta desactivada',
  body: 'Tu cuenta o tu organización fue desactivada. Contacta a soporte para más información.',
  closable: false,
  primaryLabel: 'Cerrar sesión'
}

const isAccountDisabled = (userMe, isSupportSession) =>
  !isSupportSession &&
  Boolean(userMe) &&
  (userMe.active === false || userMe.currentOrganization?.active === false)

const AuthGuard = () => {
  const { setBusiness, currentBusiness, setCurrentBusiness } = useGlobalContext()
  const { isAuthenticated, isLoaded, registrationStatus, isSupportSession, logout } = UserAuth()
  const { data: business = [], isFetched } = useQueryGetAllBusinesses()
  const { data: userMe, isFetched: isUserMeFetched } = useQueryGetUserMe({
    enabled: isAuthenticated
  })
  const organizationId = currentBusiness?.organizationId || null
  const location = useLocation()

  const {
    subscriptionModal,
    showBlockModal,
    isBlocked,
    canAccessRoute,
    handleCloseModal,
    ajustesPath
  } = useSubscriptionGuard(organizationId)

  // Cargar empresa por defecto sin bloquear el render
  useEffect(() => {
    if (!isAuthenticated || !isFetched || !isUserMeFetched) return
    if (Array.isArray(business) && business.length > 0) {
      setBusiness(business)
      if (!currentBusiness) {
        const preferredBusiness = userMe?.currentOrganizationId
          ? business.find(
              item => Number(item.organizationId) === Number(userMe.currentOrganizationId)
            )
          : undefined
        setCurrentBusiness(preferredBusiness || business[0])
      } else {
        const freshBusiness = business.find(item => Number(item.id) === Number(currentBusiness.id))
        const freshRutRepresentante = freshBusiness?.rutRepresentante ?? null
        if (
          freshBusiness &&
          freshRutRepresentante !== (currentBusiness.rutRepresentante ?? null)
        ) {
          setCurrentBusiness({
            ...currentBusiness,
            rutRepresentante: freshRutRepresentante
          })
        }
      }
    }
  }, [
    isAuthenticated,
    isFetched,
    isUserMeFetched,
    business,
    currentBusiness?.id,
    currentBusiness?.rutRepresentante,
    setBusiness,
    setCurrentBusiness,
    userMe?.currentOrganizationId
  ])

  useEffect(() => {
    const pendingQueryParams = sessionStorage.getItem('pendingQueryParams')
    const pendingPathname = sessionStorage.getItem('pendingPathname')

    if (pendingQueryParams && pendingPathname && isAuthenticated && currentBusiness) {
      sessionStorage.removeItem('pendingQueryParams')
      sessionStorage.removeItem('pendingPathname')

      window.location.href = pendingPathname + pendingQueryParams
    }
  }, [isAuthenticated, currentBusiness])

  //evita mandar al login cuando el estado de auth aún está “indeterminado”.
  if (!isLoaded) return <div />

  // si no hay sesión, redirige
  if (!isAuthenticated) {
    if (registrationStatus && registrationStatus !== REGISTRATION_STATUS.COMPLETED) {
      return <Navigate to={getRegistrationPath(registrationStatus)} replace />
    }

    if (location.search) {
      sessionStorage.setItem('pendingQueryParams', location.search)
      sessionStorage.setItem('pendingPathname', location.pathname)
    }
    return <Navigate to="/auth/signin" replace state={{ from: location }} />
  }

  if (isAccountDisabled(userMe, isSupportSession)) {
    return (
      <SubscriptionBlockModal
        isOpen={true}
        config={DISABLED_ACCOUNT_MODAL}
        onClose={() => {}}
        onPrimaryClick={logout}
      />
    )
  }

  // exiges empresa seleccionada:
  if (!currentBusiness) return <div />

  const pathname = location.pathname
  const allowed = canAccessRoute(pathname)
  const showModal =
    subscriptionModal &&
    ((isBlocked && pathname !== ajustesPath) || (!isBlocked && showBlockModal))

  return (
    <>
      {showModal && (
        <SubscriptionBlockModal
          isOpen={true}
          config={subscriptionModal}
          onClose={handleCloseModal}
          onSecondaryClick={handleCloseModal}
        />
      )}
      {allowed ? (
        <Outlet />
      ) : (
        <div className='relative'>
          <Outlet />
          <div
            className='fixed inset-0 bg-black/70 z-[1400] pointer-events-auto'
            aria-hidden
          />
        </div>
      )}
    </>
  )
}

export default AuthGuard

//Diccionario Guille:
// isAuthenticated: ¿hay sesión válida ya verificada?
// isLoaded: ya terminó la verificación inicial de auth (muy importante).
// business: arreglo (o vacío) con empresas del usuario.
// isFetched: React Query terminó al menos una vez (éxito o cache).

// isLoaded es clave: mientras sea false, no sabes todavía si hay sesión.
// Si rediriges antes de que sea true, puedes mandar al login por error.
